import { createMemo, For, Show } from "solid-js";
import type { ScanSectionKey } from "../../enums/scan";
import { relativeAge } from "../../ui/format";
import { isOpen } from "../website/issueModel";
import type { WebsiteContext } from "../website/store";
import { createDismissable } from "./createDismissable";
import {
  createCompletedRunForReport,
  scanCoverage,
  scanScopeLines,
} from "./scanCoverage";
import { ScanIcon } from "./scanIcons";
import { scanGroups } from "./scanNavigation";

export function ScanSideRail(props: {
  context: WebsiteContext;
  active: ScanSectionKey;
}) {
  const data = () => props.context.data();
  const completedRun = createCompletedRunForReport(
    props.context.websiteId,
    data,
  );
  const coverage = createMemo(() => {
    const value = data();
    return value ? scanCoverage(value, completedRun()) : null;
  });
  const scopeLines = createMemo(() => {
    const value = coverage();
    return value ? scanScopeLines(value) : [];
  });
  const groups = () => scanGroups(props.context.websiteId, data());
  const mobileNav = createDismissable({ bodyClass: "signal-mobile-nav-open" });
  const currentScope = () =>
    data()?.site.displayName ?? data()?.site.clientName ?? "Current scan";
  const activeLabel = () =>
    groups()
      .flatMap((group) => group.items)
      .find((item) => item.key === props.active)?.label ?? "Scan";

  return (
    <>
      <div class="signal-mobile-section-bar">
        <button
          ref={mobileNav.setTrigger}
          class="signal-mobile-section-trigger"
          type="button"
          aria-expanded={mobileNav.open()}
          aria-controls="signal-mobile-section-menu"
          onClick={() => mobileNav.toggle()}
        >
          <span>
            <small>Signal Scan</small>
            <strong>{activeLabel()}</strong>
          </span>
          <span aria-hidden="true">
            {mobileNav.open() ? "Close" : "Sections"}
          </span>
        </button>
      </div>
      <aside
        id="signal-mobile-section-menu"
        class={`signal-side ${mobileNav.open() ? "is-mobile-open" : ""}`}
        aria-label="Signal Scan navigation"
      >
        <button
          class="signal-mobile-nav-close"
          type="button"
          onClick={mobileNav.close}
          aria-label="Close Signal Scan navigation"
        >
          Close
        </button>
        <div>
          <div class="signal-side-kicker">Signal Scan</div>
          <nav class="signal-side-list">
            <For each={groups()}>
              {(group) => (
                <section class="signal-side-group">
                  <span>{group.label}</span>
                  <For each={group.items}>
                    {(item) => (
                      <a
                        class={`signal-side-item ${
                          props.active === item.key ? "is-active" : ""
                        }`}
                        href={item.href}
                        aria-current={
                          props.active === item.key ? "page" : undefined
                        }
                        onClick={() => mobileNav.setOpen(false)}
                      >
                        <ScanIcon name={item.icon} />
                        <span>{item.label}</span>
                        <Show when={item.badge}>
                          {(badge) => (
                            <strong class="signal-side-badge">{badge()}</strong>
                          )}
                        </Show>
                      </a>
                    )}
                  </For>
                </section>
              )}
            </For>
          </nav>
        </div>

        <div class="signal-side-footer">
          <section class="signal-scope-card" aria-label="Scan scope">
            <span>Scan scope</span>
            <strong>{currentScope()}</strong>
            <Show when={data()}>
              {(value) => (
                <>
                  <For each={scopeLines()}>
                    {(line) => <small>{line}</small>}
                  </For>
                  <small>
                    {value().issues.filter(isOpen).length} open issues
                  </small>
                </>
              )}
            </Show>
          </section>
          <Show when={data()?.lastRun?.finishedAt}>
            {(finishedAt) => (
              <div class="signal-side-updated">
                <i aria-hidden="true" />
                Scored run completed {relativeAge(finishedAt())}
              </div>
            )}
          </Show>
        </div>
      </aside>
    </>
  );
}
